/**
 * useGbusinessLocations — fetch GBP accounts + locations across every OK OAuth
 * connection in one call. Centralises the multi-account fan-out so consumer
 * pages don't repeat the `useQueries` + `useMemo` plumbing (and don't slip
 * back into the old `accounts[0]?.accountId` single-account bug).
 *
 * Backend split (post `multi-account-2`):
 *   /accounts                       → flat list, tagged with google_email
 *   /accounts/<account_id>/locations → resolves the right creds per account_id
 *
 * The hook fires one `/locations` request per account in parallel. Returned
 * lookups (`locationsByAccount`, `locationsByEmail`, `emailByLocationId`,
 * `loadingByEmail`) cover the access patterns we have today:
 *   - flat list (selects across all locations)
 *   - per-connection grouping (ConnectionList renders locations under each
 *     Google account card)
 *   - per-location email lookup (UI labels disambiguating duplicate titles)
 */
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import { listGbusinessAccounts, listGbusinessLocations } from '@/api/gbusinessService';

/**
 * @param {{ enabled?: boolean }} [opts]
 * `enabled=false` skips both the /accounts call and the per-account
 * /locations fan-out — use it when the caller already knows there are no
 * OK Google connections so we don't ping the backend just to get 412'd.
 */
export function useGbusinessLocations({ enabled = true } = {}) {
  const accountsQuery = useQuery({
    queryKey: ['gbusiness', 'accounts'],
    queryFn: listGbusinessAccounts,
    enabled,
  });
  const accounts = accountsQuery.data || [];

  // `useQueries` infers `data` as `unknown` when the queryFn isn't typed via
  // generics; cast to the SPA's location shape so the rest of the hook can
  // use `.data` without a per-call cast.
  const locationsQueries = /** @type {{ data?: any[], isLoading: boolean }[]} */ (
    useQueries({
      queries: accounts.map((a) => ({
        queryKey: ['gbusiness', 'locations', a.accountId],
        queryFn: () => listGbusinessLocations(a.accountId),
        enabled: enabled && !!a.accountId,
      })),
    })
  );

  // Stable signatures derived from the queries array — used as deps so the
  // memos below refresh exactly when query data/state changes, without
  // depending on the (re-created-every-render) queries array itself.
  const dataSig = locationsQueries.map((q) => (q.data ? q.data.length : -1)).join('|');
  const loadingSig = locationsQueries.map((q) => (q.isLoading ? '1' : '0')).join('');

  const locationsByAccount = useMemo(() => {
    /** @type {Map<string, any[]>} */
    const map = new Map();
    accounts.forEach((a, i) => {
      map.set(a.accountId, locationsQueries[i]?.data || []);
    });
    return map;
  }, [accounts, dataSig]);

  const locationsByEmail = useMemo(() => {
    /** @type {Map<string, any[]>} */
    const map = new Map();
    accounts.forEach((a) => {
      const email = a.googleEmail || '';
      if (!email) return;
      const locs = locationsByAccount.get(a.accountId) || [];
      const prev = map.get(email) || [];
      map.set(email, prev.concat(locs));
    });
    return map;
  }, [accounts, locationsByAccount]);

  const allLocations = useMemo(() => {
    const out = [];
    for (const list of locationsByAccount.values()) out.push(...list);
    return out;
  }, [locationsByAccount]);

  // Per-connection loading flag. The `accounts` array doesn't include the
  // email until /accounts resolves, so callers also need to consult
  // `isLoadingAccounts` to decide whether a connection with no entry in
  // this map is "still loading" vs "really has no accounts".
  const loadingByEmail = useMemo(() => {
    /** @type {Map<string, boolean>} */
    const map = new Map();
    accounts.forEach((a, i) => {
      const email = a.googleEmail || '';
      if (!email) return;
      const prev = map.get(email) || false;
      map.set(email, prev || !!locationsQueries[i]?.isLoading);
    });
    return map;
  }, [accounts, loadingSig]);

  const emailByLocationId = useMemo(() => {
    /** @type {Map<string, string>} */
    const map = new Map();
    for (const loc of allLocations) {
      const acc = accounts.find((a) => a.accountId === loc.accountId);
      if (acc?.googleEmail) map.set(loc.locationId, acc.googleEmail);
    }
    return map;
  }, [allLocations, accounts]);

  const isLoadingAccounts = !!accountsQuery.isLoading;
  const isLoadingLocations = locationsQueries.some((q) => q.isLoading);

  // A revoked/expired Google token surfaces as a 409 `oauth_token_revoked`
  // from /accounts (account cache stale) or a per-account /locations call.
  // The backend has, by the time we get this, already flipped that
  // connection's stored status to `reauth_required` — but those errored
  // queries look identical to "succeeded with 0 results" above (`q.data ||
  // []`), so without this flag the consumer would render the misleading "no
  // locations" banner instead of the dedicated "reconnect" one. Consumers use
  // it to refetch /auth/connections (→ banner flips) and suppress the empty
  // banner meanwhile.
  const accountsErrorCode = accountsQuery.error?.response?.data?.code;
  const reauthRequired =
    accountsErrorCode === 'oauth_token_revoked'
    || locationsQueries.some((q) => q.error?.response?.data?.code === 'oauth_token_revoked');

  return {
    accounts,
    allLocations,
    locationsByAccount,
    locationsByEmail,
    emailByLocationId,
    loadingByEmail,
    isLoadingAccounts,
    isLoadingLocations,
    isLoading: isLoadingAccounts || isLoadingLocations,
    reauthRequired,
  };
}
